import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { discontsForm } from "../../formSchemas/discounts.form";
import { IDiscounts, IDiscountType } from "../../interfaces/IDiscounts";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import { ICallAPIFunction } from "../../yoello-lib/modules/API2/types";
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext";
import DynamicForm from "../../yoello-lib/modules/dynamicForms/DynamicForms";
import EColourPanel from "../../yoello-lib/modules/dynamicForms/interfaces/EColourPanel";
import { useCurrencyState } from "../../yoello-lib/modules/hooks/useCurrencyState";
import { useSelector } from "react-redux";
import { IReduxAppState } from "../../reducers";
import { IDialogRouteChildren } from "../dialogRoutes/DialogRoute";
import { useChangedDialog } from "../../hooks/useChangedDialog";
import useCheckPermissions from "../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../const/permissions.const";
import ListLoading from "../util/Loading";
import { FormLoadingWrapper } from "../util/FormLoadingWrapper";
interface IDiscountFormData {
  type: IDiscountType;
  code?: string;
  enabled: Boolean;
  max_number_of_uses?: number | string;
  minimum_order_value?: number | string;
  description?: string;
  value?: number;
  id?: string;
}
interface IDiscountsFormDialog extends IDialogRouteChildren {
  callDiscountsAPI: ICallAPIFunction<IDiscounts>;
  venueId: string;
  itemData?: IDiscountFormData;
  discountsList?: any[];
}

export function DiscountsFormDialog({
  onClose,
  callDiscountsAPI,
  venueId,
  itemData,
  isNewItem,
  discountsList,
  onDialogClose,
  id
}: IDiscountsFormDialog) {
  const [isLoading, setIsLoading] = useState(false);
  //@ts-ignore
  const [formData, setFormData] = useState<IDiscountFormData>(itemData ? itemData : {
    type: IDiscountType.PERCENTAGE, enabled: false, code: "",
    //@ts-ignore
    value: "", description: "", minimum_order_value: "", max_number_of_uses: ""
  });
  const { t } = useTranslation();
  const currency = useCurrencyState();
  const { hasFormChanged } = useChangedDialog();

  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.DISCOUNT_AND_PROMOTIONS);

  const { code, selectedType } = useSelector(
    (state: IReduxAppState) => state.discounts
  );
  useEffect(() => {
    if (!isNewItem && !itemData) {
      setIsLoading(true);
      callDiscountsAPI(HTTPMethods.GET, { query: id }).then(({ isSuccessful, data }) => {
        if (isSuccessful) {
          setFormData(data as IDiscountFormData);
          setIsLoading(false);
          return;
        }
        onClose();
      })
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (code) {
      setFormData(prevState => ({ ...prevState, code: code }));
    }
    if (selectedType !== formData.type) {
      setFormData(prevState => ({ ...prevState, discount_type: selectedType }));
    }
    // eslint-disable-next-line
  }, [code, selectedType])
  const { sendSnackbarMessage } = useSnackbarMessages()!;

  function codeValidation(code: string) {
    if (code) {
      const data = discountsList?.filter(item => item.code === code);
      if (data && data.length > 0) {
        return false;
      }
      return true;
    }
  }

  async function onSubmit(data: Partial<IDiscounts>) {
    const minOrderValue = data.minimum_order_value ? data.minimum_order_value * 100 : 0;
    const maxNoOfUses = data.max_number_of_uses ? data.max_number_of_uses : null;
    const maxUsesPerConsumer = data.max_uses_per_consumer? data.max_uses_per_consumer : null;
    let discountValue = null;
    if (selectedType === IDiscountType.VALUE) {
      discountValue = data.value ? data.value * 100 : null;
    } else {
      discountValue = data.value ? data.value : null;
    }
    setIsLoading(true);
    setFormData(data as IDiscountFormData);
    if (isNewItem) {
      const response = await callDiscountsAPI(HTTPMethods.POST, {
        body: {
          ...data,
          minimum_order_value: minOrderValue,
          max_number_of_uses: maxNoOfUses,
          venue_id: venueId,
          type: selectedType,
          value: discountValue,
          max_uses_per_consumer:maxUsesPerConsumer
        },
      });

      if (response.isSuccessful) {
        sendSnackbarMessage(t("snackbar.DiscountCreateSuccess"), "success");
        if (onDialogClose) {
          onDialogClose();
        } else {
          onClose();
        }
      } else {
        sendSnackbarMessage(t(generateErrorMessage(response, isNewItem)), "error");
      }
    } else {
      const response = await callDiscountsAPI(HTTPMethods.PATCH, {
        query: itemData?.id ?? id,
        body: {
          enabled: data.enabled
        },
      });
      if (response.isSuccessful) {
        sendSnackbarMessage(t("snackbar.DiscountUpdateSuccess"), "success");

        if (onDialogClose) {
          onDialogClose();
        } else {
          onClose();
        }
      } else {
        sendSnackbarMessage(t(generateErrorMessage(response, isNewItem)), "error");
      }
    }
    setIsLoading(false);
  }
  return (isLoading ? <FormLoadingWrapper><ListLoading /> </FormLoadingWrapper> :
    <DynamicForm
      useFormOptions={{
        mode: "onChange", reValidateMode: "onChange"
      }}
      onHandleSubmit={onSubmit}
      formRows={discontsForm(!isNewItem, selectedType, t, currency.symbol, codeValidation)}
      formData={formData}
      disableAll={!hasPermission}
      disableAllInteraction={onFailedInteraction}
      formKeyName={"Discounts"}
      isDialog={true}
      hasDataChanged={hasFormChanged}
      formButtons={{
        leftButton: {
          type: "button",
          label: t("forms.buttons.cancel"),
          color: EColourPanel.DEFAULT,
          fullWidth: false,
          onClick: onClose,
        },
        rightButton: {
          type: "submit",
          label: isNewItem ? t("discounts.buttons.createDiscount") : t("discounts.buttons.updateDiscount"),
          color: EColourPanel.PRIMARY,
          fullWidth: false,
          disabled: isLoading || !hasPermission,
        },
      }}
    />
  );
}


function generateErrorMessage(response: any, isNewItem: boolean) {
  console.log({ isNewItem });
  const errorResponse = response?.data?.response?.data?.data;
  if (errorResponse && Array.isArray(errorResponse)) {
    if (errorResponse[0].code === "DISCOUNT_CODE_ALREADY_EXISTS") {
      return "snackbar.discountCodeExists";

    }
  }
  return isNewItem ? "snackbar.DiscountCreateFail" : "snackbar.DiscountUpdateFail";
}